import { supabase } from '../supabaseClient';

/**
 * Product Service - Handles all product-related database operations
 * Converts between snake_case (DB) and camelCase (Next.js)
 */

// Helper function to convert DB row to camelCase
const dbToProduct = (row) => {
  if (!row) return null;
  
  return {
    id: row?.id,
    partNumber: row?.part_number,
    name: row?.name,
    category: row?.category,
    equipmentType: row?.equipment_type,
    price: parseFloat(row?.price),
    stock: row?.stock,
    location: row?.location,
    handlingTime: row?.handling_time,
    deliveryDays: row?.delivery_days,
    returnPolicy: row?.return_policy,
    status: row?.status,
    images: row?.images || [],
    image: row?.image || '',
    alt: row?.alt || '',
    description: row?.description || '',
    specifications: row?.specifications || [],
    condition: row?.condition,
    brand: row?.brand,
    mpn: row?.mpn,
    countryOfOrigin: row?.country_of_origin,
    createdAt: row?.created_at,
    updatedAt: row?.updated_at,
    lastUpdated: row?.updated_at
  };
};

// Helper function to convert camelCase to DB format
const productToDb = (product) => {
  return {
    part_number: product?.partNumber,
    name: product?.name,
    category: product?.category,
    equipment_type: product?.equipmentType,
    price: product?.price,
    stock: product?.stock,
    location: product?.location,
    handling_time: product?.handlingTime,
    delivery_days: product?.deliveryDays,
    return_policy: product?.returnPolicy,
    status: product?.status,
    images: product?.images || [],
    image: product?.images?.[0] || '',
    alt: `${product?.name} spare part`,
    description: product?.description || '',
    specifications: product?.specifications || [],
    condition: product?.condition,
    brand: product?.brand,
    mpn: product?.mpn,
    country_of_origin: product?.countryOfOrigin
  };
};

export const productService = {
  /**
   * Get all products
   */
  async getAll() {
    try {
      const { data, error } = await supabase?.from('products')?.select('*')?.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return {
        success: true,
        data: data?.map(dbToProduct) || []
      };
    } catch (error) {
      console.error('Error fetching products:', error);
      return {
        success: false,
        error: error?.message || 'Failed to fetch products',
        data: []
      };
    }
  },

  /**
   * Get product by ID
   */
  async getById(id) {
    try {
      const { data, error } = await supabase?.from('products')?.select('*')?.eq('id', id)?.single();
      
      if (error) throw error;
      
      return {
        success: true,
        data: dbToProduct(data)
      };
    } catch (error) {
      console.error('Error fetching product:', error);
      return {
        success: false,
        error: error?.message || 'Failed to fetch product',
        data: null
      };
    }
  },

   /**
   * Create new product
   */
  async create(product) {
  try {
    //console.log("🟢 Creating new product...");
    //console.log("🟢 Creating product...", product);

    const dbProduct = productToDb(product);
    //console.log("🟡 DB format:", dbProduct);

    //console.log("🔵 Calling API route insert...");

    const res = await fetch("/api/admin/products/create", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(dbProduct),
    });

    const result = await res.json();

    //console.log("🟣 API response:", result);

    if (!res.ok || !result?.success) {
      return {
        success: false,
        error: result?.error || "Failed to create product",
        data: null,
      };
    }

    return {
      success: true,
      data: dbToProduct(result?.data),
    };
  } catch (err) {
    //console.error("🔴 Error creating product:", err);
    return {
      success: false,
      error: err?.message || "Failed to create product",
      data: null,
    };
  }
},
  /**
   * Update product
   */
  async update(id, updates) {
  try {
    //console.log("🟢 Updating product...", id, updates);

    const dbUpdates = productToDb(updates);

    const res = await fetch("/api/admin/products/update", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id, ...dbUpdates }),
    });

    const result = await res.json();
    //console.log("🟣 Update API response:", result);

    if (!res.ok || !result?.success) {
      return {
        success: false,
        error: result?.error || "Failed to update product",
        data: null,
      };
    }

    return {
      success: true,
      data: dbToProduct(result?.data),
    };
  } catch (err) {
    //console.error("🔴 Error updating product:", err);
    return {
      success: false,
      error: err?.message || "Failed to update product",
      data: null,
    };
  }
},
  /**
   * Delete product
   */
  async delete(id) {
  try {
    //console.log("🟢 Deleting product...", id);

    const res = await fetch("/api/admin/products/delete", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id }),
    });

    const result = await res.json();
    //console.log("🟣 Delete API response:", result);

    if (!res.ok || !result?.success) {
      return {
        success: false,
        error: result?.error || "Failed to delete product",
      };
    }

    return { success: true };
  } catch (err) {
    //console.error("🔴 Error deleting product:", err);
    return {
      success: false,
      error: err?.message || "Failed to delete product",
    };
  }
},

  /**
   * Bulk update product status
   */
  async bulkUpdateStatus(ids, status) {
    try {
      const { data, error } = await supabase?.from('products')?.update({ status })?.in('id', ids)?.select();
      
      if (error) throw error;
      
      return {
        success: true,
        data: data?.map(dbToProduct) || []
      };
    } catch (error) {
      //console.error('Error bulk updating products:', error);
      return {
        success: false,
        error: error?.message || 'Failed to update products',
        data: []
      };
    }
  },

  /**
   * Bulk delete products
   */
  async bulkDelete(ids) {
    try {
      const { error } = await supabase?.from('products')?.delete()?.in('id', ids);
      
      if (error) throw error;
      
      return {
        success: true
      };
    } catch (error) {
      //console.error('Error bulk deleting products:', error);
      return {
        success: false,
        error: error?.message || 'Failed to delete products'
      };
    }
  },

  /**
   * Search products
   */
  async search(query) {
    try {
      const { data, error } = await supabase?.from('products')?.select('*')?.or(`part_number.ilike.%${query}%,name.ilike.%${query}%,category.ilike.%${query}%`)?.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return {
        success: true,
        data: data?.map(dbToProduct) || []
      };
    } catch (error) {
      console.error('Error searching products:', error);
      return {
        success: false,
        error: error?.message || 'Failed to search products',
        data: []
      };
    }
  },

  /**
   * Filter products by category
   */
  async filterByCategory(category) {
    try {
      const { data, error } = await supabase?.from('products')?.select('*')?.eq('category', category)?.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return {
        success: true,
        data: data?.map(dbToProduct) || []
      };
    } catch (error) {
      console.error('Error filtering products:', error);
      return {
        success: false,
        error: error?.message || 'Failed to filter products',
        data: []
      };
    }
  },

  /**
   * Filter products by status
   */
  async filterByStatus(status) {
    try {
      const { data, error } = await supabase?.from('products')?.select('*')?.eq('status', status)?.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return {
        success: true,
        data: data?.map(dbToProduct) || []
      };
    } catch (error) {
      console.error('Error filtering products:', error);
      return {
        success: false,
        error: error?.message || 'Failed to filter products',
        data: []
      };
    }
  }
  ,
/**
 * Get paginated products (for Admin Dashboard)
 */
async getPaginated(from, to) {
  try {
    const { data, error, count } = await supabase
      .from('products')
      .select('*', { count: 'exact' })
      .range(from, to)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return {
      success: true,
      data: data?.map(dbToProduct) || [],
      count,
    };
  } catch (error) {
    console.error('Error fetching paginated products:', error);

    return {
      success: false,
      error: error?.message || 'Failed to fetch products',
      data: [],
      count: 0,
    };
  }
},

//filteredpaginated
async getFilteredPaginated({
  from,
  to,
  search,
  category,
  status,
}) {
  try {
    let query = supabase
      .from('products')
      .select('*', { count: 'exact' });

    //  Search
    if (search) {
      query = query.or(
        `part_number.ilike.%${search}%,name.ilike.%${search}%,category.ilike.%${search}%`
      );
    }

    //  Category
    if (category && category !== 'all') {
      query = query.eq('category', category);
    }

    //  Status
    if (status && status !== 'all') {
      query = query.eq('status', status);
    }

    // Pagination
    query = query
      .range(from, to)
      .order('created_at', { ascending: false });

    const { data, error, count } = await query;

    if (error) throw error;

    // Fetch all categories
    const { data: cats } = await supabase
      .from('products')
      .select('category');

    const allCategories = [
      ...new Set(cats?.map((c) => c.category).filter(Boolean)),
    ];

    return {
      success: true,
      data: data.map(dbToProduct),
      count,
      allCategories, //  important
    };
  } catch (err) {
    console.error(err);

    return {
      success: false,
      error: err.message,
      data: [],
      count: 0,
      allCategories: [],
    };
  }
},

//for all category
// async getPaginated(from, to) {
//   try {
//     // Get products (paginated)
//     const { data, error, count } = await supabase
//       .from('products')
//       .select('*', { count: 'exact' })
//       .range(from, to)
//       .order('created_at', { ascending: false });

//     if (error) throw error;

//     //  Get ALL categories (for filter dropdown)
//     const { data: cats } = await supabase
//       .from('products')
//       .select('category');

//     const allCategories = [
//       ...new Set(cats?.map((c) => c.category).filter(Boolean)),
//     ];

//     return {
//       success: true,
//       data: data?.map(dbToProduct) || [],
//       count,
//       allCategories, //  important
//     };
//   } catch (error) {
//     console.error('Error fetching paginated products:', error);

//     return {
//       success: false,
//       error: error?.message || 'Failed to fetch products',
//       data: [],
//       count: 0,
//       allCategories: [],
//     };
//   }
// },
 };